import { useState } from "react";
import { Check, ChevronsUpDown, Globe, Plus, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSite } from "../hooks/useSite";
import { addSite } from "../lib/api";

const PRIMARY = "#018D4C";

// Active-site picker above the org switcher. Adding sites is capped by MAX_SITES_PER_ORG.
export default function SiteSwitcher() {
  const { sites, site, maxSites, switchSite, reload } = useSite();
  const [adding, setAdding] = useState(false);
  const atLimit = sites.length >= maxSites;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="site-switcher"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-sidebar-border bg-sidebar hover:bg-sidebar-accent text-left"
          >
            <Globe size={14} style={{ color: PRIMARY }} />
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-sidebar-foreground">{site?.name || "No site"}</span>
            <ChevronsUpDown size={14} className="text-zinc-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400">Sites</DropdownMenuLabel>
          {sites.map((s) => (
            <DropdownMenuItem key={s.id} onSelect={() => switchSite(s.id)} className="gap-2">
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm">{s.name}</span>
                {s.base_url && <span className="block truncate text-[10px] text-zinc-400">{s.base_url}</span>}
              </span>
              {s.id === site?.id && <Check size={14} style={{ color: PRIMARY }} />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={atLimit} onSelect={() => setAdding(true)} className="gap-2">
            <Plus size={14} />
            <span className="flex-1">Add site</span>
          </DropdownMenuItem>
          {atLimit && (
            <p className="px-2 pb-1.5 text-[10px] text-zinc-400">
              Your workspace includes {maxSites} site{maxSites === 1 ? "" : "s"}. More sites come with paid plans.
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AddSiteDialog
        open={adding}
        onOpenChange={setAdding}
        onAdded={async (created) => {
          await reload();
          switchSite(created.id);
          setAdding(false);
        }}
      />
    </>
  );
}

function AddSiteDialog({ open, onOpenChange, onAdded }) {
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onAdded(await addSite({ name: name.trim(), base_url: baseUrl.trim() }));
      setName("");
      setBaseUrl("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add the site.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a site</DialogTitle>
          <DialogDescription>Each site has its own CMS, Search Console and data.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input
            required value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name or site name"
            className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2"
          />
          <input
            type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://example.com (optional)"
            className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit" disabled={busy || !name.trim()}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-white rounded-lg disabled:opacity-50"
            style={{ backgroundColor: PRIMARY }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add site
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
